/*
 * AdvicePrep endpoints, per ADVICEPREP_SIGNUP_SPEC.md section 5.
 *
 * The paths keep the old `adviseprep` spelling: the rename was UI-only and the
 * server still routes on it. Renaming them here 404s every call.
 *
 * This API sells the flat monthly fee and nothing else. Connection counts and
 * usage charges belong to the repo that owns the Xero connections, so nothing
 * here accepts or returns a connection count.
 */
import { apiRequest } from '@/lib/httpClient'
import { couponDetail, findCoupon, validateCoupon } from '@/features/auth/api'
import {
  ADVICEPREP_COUPON_PRODUCT,
  advicePrepAppliedCoupon,
  type AdvicePrepAppliedCoupon,
} from './pricing'
import type {
  AdvicePrepCancelResult,
  AdvicePrepClientPayload,
  AdvicePrepClientResult,
  AdvicePrepExistsResult,
  AdvicePrepOutstandingInvoice,
  AdvicePrepPayInvoiceResult,
  AdvicePrepResumeResult,
  AdvicePrepSubscribeResult,
} from './types'

/**
 * The same check the signup wizard runs at its pricing step, behind one call so
 * the Billing upsell never has to know the product discriminator or the shape
 * the coupon list comes back in. Null means the server did not recognise it.
 */
export async function validateAdvicePrepCoupon(code: string): Promise<AdvicePrepAppliedCoupon | null> {
  const coupons = await validateCoupon({ Code: code, Product: ADVICEPREP_COUPON_PRODUCT })
  const match = findCoupon(coupons, code)
  return match ? advicePrepAppliedCoupon(code, couponDetail(match)) : null
}

/** Spec 5.1b — POST open/api/ui/signup/adviseprep/exists */
export async function checkAdvicePrepExists(input: {
  email: string
  companyName: string
}): Promise<AdvicePrepExistsResult> {
  const response = await apiRequest<Partial<AdvicePrepExistsResult>>('ui/signup/adviseprep/exists', {
    method: 'POST',
    open: true,
    body: input,
  })
  return {
    companyExists: Boolean(response?.companyExists),
    emailExists: Boolean(response?.emailExists),
  }
}

/**
 * Spec 5.1c — POST open/api/ui/signup/adviseprep/client
 *
 * Creates the client, the user, the Cognito login and the Stripe customer, and
 * signs the user in. No platform row and no subscription (D-5, D-13).
 *
 * The wire format returns the tokens flat; they are gathered into `session`
 * here so a partial set cannot reach the caller looking usable.
 */
export async function createAdvicePrepClient(payload: AdvicePrepClientPayload): Promise<AdvicePrepClientResult> {
  const response = await apiRequest<{
    clientId?: string
    userId?: string
    stripeCustomerId?: string
    idToken?: string
    refreshToken?: string
    expiry?: number
    error?: string | null
  }>('ui/signup/adviseprep/client', {
    method: 'POST',
    open: true,
    body: payload,
  })

  if (response.error || !response.clientId) {
    return { error: response.error || 'Unable to create account' }
  }

  const { idToken, refreshToken, expiry } = response
  return {
    clientId: response.clientId,
    userId: response.userId || '',
    stripeCustomerId: response.stripeCustomerId || '',
    session: idToken && refreshToken && expiry != null ? { idToken, refreshToken, expiry } : undefined,
  }
}

/**
 * Spec 5.2 — POST api/user/ui/stripe/adviseprep/subscription
 *
 * Two callers: the signup wizard at step 4, and the Billing upsell for an
 * existing customer (D-14). The Billing path has no `customerId` or
 * `paymentMethodId` to hand — nothing in the billing overview exposes either —
 * so it sends `clientId` alone and the server resolves the card on file.
 */
export async function subscribeAdvicePrep(input: {
  clientId: string
  promoCode?: string
  customerId?: string
  paymentMethodId?: string
}): Promise<AdvicePrepSubscribeResult> {
  return apiRequest<AdvicePrepSubscribeResult>('user/ui/stripe/adviseprep/subscription', {
    method: 'POST',
    body: input,
  })
}

/**
 * POST api/user/ui/stripe/adviseprep/subscription/subscribe-now
 *
 * Ends a promo-code trial today and charges the full monthly fee straight away.
 * A declined card is a 400 that leaves the trial untouched; a 400 with code
 * `payment_method_required` means there is no card to charge yet.
 */
export async function subscribeAdvicePrepNow(input: {
  clientId: string
  paymentMethodId?: string
}): Promise<AdvicePrepSubscribeResult> {
  return apiRequest<AdvicePrepSubscribeResult>('user/ui/stripe/adviseprep/subscription/subscribe-now', {
    method: 'POST',
    body: input,
  })
}

/** Spec 5.5 — POST api/user/ui/stripe/adviseprep/subscription/cancel */
export async function cancelAdvicePrepSubscription(input: {
  clientId: string
}): Promise<AdvicePrepCancelResult> {
  return apiRequest<AdvicePrepCancelResult>('user/ui/stripe/adviseprep/subscription/cancel', {
    method: 'POST',
    body: input,
  })
}

/** Spec 5.7 — POST api/user/ui/stripe/adviseprep/subscription/resume */
export async function resumeAdvicePrepSubscription(input: {
  clientId: string
}): Promise<AdvicePrepResumeResult> {
  return apiRequest<AdvicePrepResumeResult>('user/ui/stripe/adviseprep/subscription/resume', {
    method: 'POST',
    body: input,
  })
}

/** Spec 5.9 — GET api/user/ui/stripe/adviseprep/invoice/outstanding */
export async function fetchAdvicePrepOutstandingInvoice(clientId: string): Promise<AdvicePrepOutstandingInvoice> {
  return apiRequest<AdvicePrepOutstandingInvoice>('user/ui/stripe/adviseprep/invoice/outstanding', {
    method: 'GET',
    query: { clientId },
  })
}

/**
 * Spec 5.9 — POST api/user/ui/stripe/adviseprep/invoice/pay
 *
 * Attaching a card does not settle the unpaid invoice. Without this the
 * subscription stays past_due until Stripe's dunning retry and the paywall
 * appears to ignore what the customer just did.
 */
export async function payAdvicePrepOutstandingInvoice(input: {
  clientId: string
}): Promise<AdvicePrepPayInvoiceResult> {
  return apiRequest<AdvicePrepPayInvoiceResult>('user/ui/stripe/adviseprep/invoice/pay', {
    method: 'POST',
    body: input,
  })
}
